import { css, styled } from 'styled-components';

import { theme } from '../../../styles';
import { useTabbing } from '../../../utils';
import { useMediaMin } from '../../Media';
import { Text } from '../../Text';

type RadioButtonProps = {
  id: string;
  name: string;
  value: string;
  label?: string;
  labelColor?: 'black90' | 'white';
  checked: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  error?: boolean;
  disabled?: boolean;
  checkMarkTopLine?: boolean;
};

const radioCheckMark = css`
  content: '';
  box-sizing: border-box;
  position: absolute;
  left: 0;
  top: 0;
  width: inherit;
  height: inherit;
  border-radius: 100%;
  background-color: ${theme.colors.white};
`;

const StyledInputLabel = styled.label<{ checkMarkTopLine?: boolean; disabled?: boolean }>`
  display: inline-flex;
  align-items: ${props => (props.checkMarkTopLine ? 'start' : 'center')};
  cursor: pointer;
  ${props =>
    props.disabled &&
    css`
      cursor: not-allowed;
      opacity: 0.7;
    `}
`;

const StyledRadioCheckMark = styled.div<{
  disabled?: boolean;
  isDesktop: boolean;
  withLabel: boolean;
  hasError?: boolean;
  checkMarkTopLine?: boolean;
}>`
  flex-shrink: 0;
  position: relative;
  margin-top: ${props => (props.checkMarkTopLine && props.isDesktop ? 0.25 : 0)}rem;
  ${props => props.withLabel && 'margin-right: 0.625rem;'};
  width: ${props => (props.isDesktop ? 1.25 : 1.875)}rem;
  height: ${props => (props.isDesktop ? 1.25 : 1.875)}rem;
  &::before {
    ${radioCheckMark}
    border: 1px solid ${props => (props.hasError ? theme.colors.burgundy : theme.colors.black)};
    transition: border 0.25s ease-out;
    ${props =>
      props.disabled &&
      css`
        cursor: not-allowed;
      `}
  }
  &::after {
    ${radioCheckMark}
    transform: scale(0.6);
    transition: background-color 0.25s ease-out;
    ${props =>
      props.disabled &&
      css`
        cursor: not-allowed;
      `}
  }
`;

const StyledRadioInput = styled.input<{ isTabbing: boolean; labelColor: string }>`
  position: absolute;
  width: 0;
  height: 0;
  opacity: 0;
  cursor: inherit;
  appearance: none;
  outline: none;
  &:checked + ${StyledRadioCheckMark}::before {
    background-color: ${theme.colors.white};
  }
  &:checked + ${StyledRadioCheckMark}::after {
    background-color: ${theme.colors.black};
  }
  &:focus + ${StyledRadioCheckMark}::before {
    box-shadow: 0 0 4px ${theme.colors.darkBlue};
    ${props =>
      props.isTabbing &&
      css`
        outline: ${props.labelColor === 'white' ? theme.colors.white : theme.colors.darkBlue} solid 2px;
        outline-offset: 5px;
      `}
  }
  &:disabled + ${StyledRadioCheckMark}::before {
    border: 1px solid ${theme.colors.black30};
    cursor: not-allowed;
  }
`;

const StyledRadioLabel = styled(Text)`
  user-select: none;
`;

export const RadioButton = ({
  id,
  value,
  name,
  checked,
  onChange,
  labelColor = 'black90',
  label = '',
  error,
  disabled,
  checkMarkTopLine,
}: RadioButtonProps) => {
  const isDesktop = useMediaMin(theme.breakpoint.lg);
  const isTabbing = useTabbing();

  return (
    <StyledInputLabel checkMarkTopLine={checkMarkTopLine} data-testid={`test-${id}`} htmlFor={id} disabled={disabled}>
      <StyledRadioInput
        id={id}
        name={name}
        type="radio"
        value={value}
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        labelColor={labelColor}
        isTabbing={isTabbing}
      />
      <StyledRadioCheckMark
        withLabel={!!label}
        isDesktop={isDesktop}
        hasError={!checked && error}
        disabled={disabled}
        checkMarkTopLine={checkMarkTopLine}
      />
      {label && (
        <StyledRadioLabel renderAs="span" size={isDesktop ? 'standard' : 'medium'} color={labelColor}>
          {label}
        </StyledRadioLabel>
      )}
    </StyledInputLabel>
  );
};
